import { useCallback } from 'react';
import { DateTime, Duration } from 'luxon';
import {
  CalculateRentalPriceRequest,
  isErrorCalculatePriceResult,
} from '../../../../clients/quipli-service/pricing/pricing';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../util/toasts';
import { convertToMoney } from '../../../../util/util';
import { Location } from '../../../../clients/quipli-service/location/location';
import { Currency } from 'dinero.js';

export interface UseRentalPriceCalculatorProps {
  location: Location;
  isTaxInclusive: boolean;
  isOrderCycleBilled: boolean;
  cycleBillingTerms?: { cycleLength: Duration };
  setTaxedUnitPrice?: (price: number) => void;
  setTotalTaxPercentage?: (percentage: number) => void;
}

export interface UseRentalPriceCalculatorReturn {
  calculateRentalPrice: (
    request: CalculatorPriceRequest,
  ) => Promise<{ customPrice?: number; totalPrice?: number; taxedPrice?: number; error?: string }>;
}

export type CalculatorPriceRequest = Omit<CalculateRentalPriceRequest, 'end'> & { end?: DateTime };

// Shared hook for calculating rental prices (used by product item dates and rental rates)
export const useRentalPriceCalculator = ({
  location,
  isTaxInclusive,
  isOrderCycleBilled,
  cycleBillingTerms,
  setTaxedUnitPrice,
  setTotalTaxPercentage,
}: UseRentalPriceCalculatorProps): UseRentalPriceCalculatorReturn => {
  const { calculatePriceAsAdmin } = useAPI();

  const calculateRentalPrice = useCallback(
    async (request: CalculatorPriceRequest) => {
      const end =
        isOrderCycleBilled || !request.end
          ? request.start.plus(cycleBillingTerms?.cycleLength || { days: 28 })
          : request.end;

      try {
        const res = await calculatePriceAsAdmin({ ...request, locationId: location.id, end });

        if (isErrorCalculatePriceResult(res)) {
          toast(res.error, 'error');
          return { error: res.error };
        }

        const newCustomPrice = convertToMoney(res.totalPrice / 100, location.currency as Currency);

        // Update tax information if setters are provided
        if (setTaxedUnitPrice && !isTaxInclusive && res.taxedPrice) {
          setTaxedUnitPrice(res.taxedPrice / 100);
        }

        if (setTotalTaxPercentage && !isTaxInclusive && res.taxPercentage !== undefined) {
          setTotalTaxPercentage(res.taxPercentage);
        }

        return {
          customPrice: newCustomPrice.getAmount(),
          totalPrice: res.totalPrice,
          taxedPrice: res.taxedPrice,
        };
      } catch (e: any) {
        toast(e.message, 'error');
        return { error: e.message };
      }
    },
    [
      location,
      isTaxInclusive,
      isOrderCycleBilled,
      cycleBillingTerms,
      calculatePriceAsAdmin,
      setTaxedUnitPrice,
      setTotalTaxPercentage,
    ],
  );

  return {
    calculateRentalPrice,
  };
};
